// For non multi-currency currencies, it needs to ISO 4217 so it works well with Intl.NumberFormat in the Frontend.

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#currency_2
export enum Currency {
  USD = 'USD', // US dollars
  EUR = 'EUR', // Euros
  GBP = 'GBP', // British pounds
  AUD = 'AUD', // Aussie dollars
  // Some data providers like PayPal has multi-currency accounts where
  // currency on account is declared as 'XXX' but transactions and
  // account balances do have valid currencies USD/EUR/...
  MULTI_CURRENCY = 'MULTI_CURRENCY',
}

export const CurrencyDetails = {
  [Currency.EUR]: {
    decimalPlaces: 2,
  },
  [Currency.USD]: {
    decimalPlaces: 2,
  },
  [Currency.GBP]: {
    decimalPlaces: 2,
  },
  [Currency.AUD]: {
    decimalPlaces: 2,
  },
  [Currency.MULTI_CURRENCY]: {
    decimalPlaces: 2,
  },
};

export enum SeapointAccountCurrency {
  GBP = 'GBP',
  EUR = 'EUR',
}

export enum LegalEntityCountry {
  GB = 'GB',
  IE = 'IE',
  DE = 'DE',
}

export enum TermsAndConditionsCountry {
  EU = 'EU',
  GB = 'GB',
}

/**
 * Enum for Account
 */
export enum AccountType {
  BANKING = 'BANKING',
  INVESTMENT = 'INVESTMENT',
}

export enum AccountStatus {
  ACTIVE = 'ACTIVE', // Account is fully operational
  CLOSED = 'CLOSED', // Account has been permanently closed
  INACTIVE = 'INACTIVE', // Account is temporarily suspended
  PENDING = 'PENDING', // Account is awaiting activation
}

/**
 * WealthKernel bank account status enum
 * @see https://wealthkernel.stoplight.io/docs/guides/f56c2d090d0c3-bank-account-basics
 */
export enum WealthKernelBankAccountStatus {
  PENDING = 'Pending',
  ACTIVE = 'Active',
  SUSPENDED = 'Suspended',
  INACTIVE = 'Inactive',
}

/**
 * WealthKernel investment account status enum
 * @see https://wealthkernel.stoplight.io/docs/api/2021-05-17/operations/list-accounts
 */
export enum WealthKernelInvestmentAccountStatus {
  PENDING = 'Pending',
  ACTIVE = 'Active',
  SUSPENDED = 'Suspended',
  CLOSING = 'Closing',
  CLOSED = 'Closed',
}

export enum WealthKernelInvestmentPortfolioStatus {
  CREATED = 'Created',
  ACTIVE = 'Active',
  CLOSING = 'Closing',
  CLOSED = 'Closed',
}

export enum WealthKernelInvestmentDepositStatus {
  CREATED = 'Created',
  SETTLED = 'Settled',
  CANCELLING = 'Cancelling',
  CANCELLED = 'Cancelled',
  REJECTED = 'Rejected',
}

export enum WealthKernelOrderStatus {
  PENDING = 'Pending',
  OPEN = 'Open',
  MATCHED = 'Matched',
  REJECTED = 'Rejected',
  CANCELLING = 'Cancelling',
  CANCELLED = 'Cancelled',
}

export enum WealthKernelOrderSide {
  BUY = 'Buy',
  SELL = 'Sell',
}

export enum WealthKernelFillStatus {
  MATCHED = 'Matched',
  CANCELLED = 'Cancelled',
}

export enum AccountState {
  LINKING = 'LINKING',
  LINKED = 'LINKED',
  UNLINKED = 'UNLINKED',
  SYNCING = 'SYNCING',
  SYNCED = 'SYNCED',
  SYNC_ERROR = 'SYNC_ERROR',
}

/**
 * Enum for Participant Types
 */
export enum ParticipantType {
  VENDOR = 'VENDOR', // Vendor/supplier providing goods or services
  BENEFICIARY = 'BENEFICIARY', // Entity sending or receiving payment
  EMPLOYEE = 'EMPLOYEE', // Employee receiving payroll or expense reimbursement
  TREASURY = 'TREASURY', // Treasury participant
}

export enum Workflow {
  VENDOR_ENRICHMENT = 'VENDOR_ENRICHMENT',
  TRANSACTION_SYNC = 'TRANSACTION_SYNC',
  BENEFICIARY_EXTRACTION = 'BENEFICIARY_EXTRACTION',
  DUPLICATE_VENDOR_MERGE = 'DUPLICATE_VENDOR_MERGE',
  TRANSACTION_VENDOR_JUDGE = 'TRANSACTION_VENDOR_JUDGE',
  INVOICE_JUDGE = 'INVOICE_JUDGE',
  INVOICE_IS_PAID = 'INVOICE_IS_PAID',
  EXTRACT_GMAIL_INVOICES = 'EXTRACT_GMAIL_INVOICES',
  ACCOUNT_UNLINK = 'ACCOUNT_UNLINK',
}

export enum WorkflowTriggerSource {
  CRON_JOB = 'CRON_JOB',
  WEBHOOK = 'WEBHOOK',
  USER_MANUAL_TRIGGER = 'USER_MANUAL_TRIGGER',
  ADMIN_MANUAL_TRIGGER = 'ADMIN_MANUAL_TRIGGER',
  OPEN_BANKING_CONNECTION = 'OPEN_BANKING_CONNECTION',
  SYSTEM_AUTOMATED_TRIGGER = 'SYSTEM_AUTOMATED_TRIGGER',
}

export enum AccountSyncEvents {
  LINK_START = 'LINK_START',
  LINK_END = 'LINK_END',
  SYNC_START = 'SYNC_START',
  SYNC_END = 'SYNC_END',
  LINK_ERROR = 'LINK_ERROR',
  SYNC_ERROR = 'SYNC_ERROR',
  UNLINK = 'UNLINK',
}

export enum BeneficiaryCreatedByType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

/** Beneficiary enums */
export enum BeneficiaryType {
  SCAN = 'SCAN',
  IBAN = 'IBAN',
}

export enum BeneficiaryAccountType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

/**
 * Eligible countries for beneficiaries EU SEPA Countries + United Kingdom
 * TODO: Validate the enum with Modulr
 */
export enum BeneficiaryCountry {
  IE = 'IE',
  GB = 'GB',
  AT = 'AT',
  BE = 'BE',
  BG = 'BG',
  HR = 'HR',
  CH = 'CH',
  CY = 'CY',
  CZ = 'CZ',
  DK = 'DK',
  EE = 'EE',
  FI = 'FI',
  FR = 'FR',
  DE = 'DE',
  GR = 'GR',
  HU = 'HU',
  IS = 'IS',
  IT = 'IT',
  LI = 'LI',
  LV = 'LV',
  LT = 'LT',
  LU = 'LU',
  MC = 'MC',
  MT = 'MT',
  NL = 'NL',
  NO = 'NO',
  PL = 'PL',
  PT = 'PT',
  RO = 'RO',
  SK = 'SK',
  SI = 'SI',
  SM = 'SM',
  ES = 'ES',
  SE = 'SE',
}

/** Payment Type */
export enum PaymentDestinationType {
  ACCOUNT = 'ACCOUNT',
  BENEFICIARY = 'BENEFICIARY',
  EMPLOYEE = 'EMPLOYEE',
  IBAN = 'IBAN',
  SCAN = 'SCAN',
}

/**
 * Type of payment indicating the direction and nature of the payment.
 */
export enum PaymentType {
  /** Payment received into an account */
  INBOUND = 'INBOUND',
  /** Payment sent from an account */
  OUTBOUND = 'OUTBOUND',
  /** Return of a previous payment */
  RETURN = 'RETURN',
}

/**
 * Status of a payment throughout its lifecycle.
 * Includes both transient and final states.
 */
export enum PaymentStatus {
  /** Initial state when payment is first created in our system */
  CREATED = 'CREATED',
  /** Transaction is being processed by the financial service provider */
  PROCESSING = 'PROCESSING',
  /** Transaction completed successfully */
  SUCCEEDED = 'SUCCEEDED',
  /** Transaction failed to complete */
  FAILED = 'FAILED',
}

export enum BulkPaymentStatus {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  PARTIALLY_SUCCEEDED = 'PARTIALLY_SUCCEEDED',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export enum BulkPaymentType {
  PAYROLL = 'PAYROLL',
}

/**
 * Status of a payment authorisation throughout its lifecycle.
 * Includes both transient and final states.
 */
export enum AuthorisationStatus {
  /** Authorisation request has been created but not yet approved/rejected */
  PENDING = 'PENDING',
  /** Payment has been explicitly authorised by the user */
  AUTHORIZED = 'AUTHORIZED',
  /** Payment was rejected during the authorisation process */
  REJECTED = 'REJECTED',
  /** Payment doesn't require authorisation */
  NOT_REQUIRED = 'NOT_REQUIRED',
  /** Payment authorization failed */
  ERROR = 'ERROR',
  /** Authorization has been revoked by the user */
  REVOKED = 'REVOKED',
}

/**
 * Integration adapters used to connect with financial service providers. Each adapter implements specific API integration requirements.
 */
export enum FinancialServiceProviderAdapters {
  GOCARDLESS = 'GOCARDLESS',
  GOCARDLESS_DEMO_DATA = 'GOCARDLESS_DEMO_DATA',
  MODULR = 'MODULR',
  YAPILY = 'YAPILY',
}

export enum BaaSProviders {
  MODULR = 'MODULR',
}

export enum EvidenceType {
  INVOICE = 'invoice',
  PAYROLL_SCHEDULE = 'payroll_schedule',
}

export enum InvoicePaidByType {
  SYSTEM = 'SYSTEM',
  USER = 'USER',
  PAYMENT = 'PAYMENT',
}

export enum EvidenceSource {
  EMAIL_SCAN = 'EMAIL_SCAN',
  EMAIL_FORWARD = 'EMAIL_FORWARD',
  UPLOAD = 'UPLOAD',
  PHOTO = 'PHOTO',
}

export enum EvidenceStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  PROCESSED = 'PROCESSED',
  IS_PAID_PROCESSED = 'IS_PAID_PROCESSED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/**
 * Represents the synchronization state of an entity with external data sources.
 * Used to track the current state of data synchronization processes.
 */
export enum SyncStatus {
  /** Initial state. Entity is ready for sync or scheduled for next sync attempt */
  PENDING = 'PENDING',
  /** Sync is actively running. Prevents concurrent sync attempts */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Last sync completed successfully. Data is up to date */
  COMPLETE = 'COMPLETE',
  /** Last sync attempt failed. Check syncError for details and nextSyncAt for retry timing */
  FAILED = 'FAILED',
}

/**
 * Define valid syncable entity types.
 */
export enum SyncableEntityType {
  ACCOUNT = 'ACCOUNT',
  COMPANY = 'COMPANY',
  INVOICE_EVIDENCE = 'INVOICE_EVIDENCE',
  VENDOR = 'VENDOR',
  TRANSACTION = 'TRANSACTION',
}

/**
 * Represents the type of entity in an external system (e.g., Xero, QuickBooks).
 * Used to categorize external entities mapped to Seapoint entities.
 */
export enum ExternalEntityType {
  /** A bank account in the external system */
  ACCOUNT = 'ACCOUNT',
  /** A contact/vendor/supplier in the external system */
  CONTACT = 'CONTACT',
  /** An invoice in the external system */
  INVOICE = 'INVOICE',
  /** A transaction/payment in the external system */
  TRANSACTION = 'TRANSACTION',
}

/**
 * Represents the processing status of a transaction. Used to track the
 * lifecycle of financial transactions.
 */
export enum TransactionStatus {
  /** Transaction has been confirmed and settled. */
  BOOKED = 'BOOKED',
  /**
   * Transaction is in process but not yet settled. May affect available
   * balance but not booked balance.
   */
  PENDING = 'PENDING',
  /**
   * Transaction has been failed by the financial service provider
   * and will not be settled.
   * @see https://modulr.readme.io/docs/payment-statuses#error-statuses
   */
  FAILED = 'FAILED',
}

/** Categorizes the type of financial transaction. */
export enum TransactionType {
  /** Payment made using a card */
  CARD_PAYMENT = 'CARD_PAYMENT',
  /** Refund of a previous card payment */
  CARD_REFUND = 'CARD_REFUND',
  /** Disputed card transaction resulting in funds being returned */
  CARD_CHARGEBACK = 'CARD_CHARGEBACK',

  /** Currency exchange transaction */
  EXCHANGE = 'EXCHANGE',

  /** Direct Debit */
  DIRECT_DEBIT = 'DIRECT_DEBIT',

  /** Bank transfer between accounts */
  TRANSFER = 'TRANSFER',
  /** Refund of a previous transfer */
  REFUND = 'REFUND',

  /** Miscellaneous transaction type. Used when transaction doesn't fit other categories. */
  OTHER = 'OTHER',
}

export enum RefreshDataEventType {
  INCREMENTAL = 'INCREMENTAL',
  FULL = 'FULL',
}

export enum RefreshDataDeduplicationKeys {
  INCREMENTAL_SYNC_AND_AI_ENRICHMENT_PIPELINE = 'INCREMENTAL_SYNC_AND_AI_ENRICHMENT_PIPELINE',
  ACCOUNT_BALANCE_CHANGE = 'ACCOUNT_BALANCE_CHANGE',
  MODULR_TRANSACTION_CREATED = 'MODULR_TRANSACTION_CREATED',
}

/**
 * Document types enum
 */
export enum DocumentType {
  MODULR_ONBOARDING = 'MODULR_ONBOARDING',
  INVOICE = 'INVOICE',
  PAYROLL_SCHEDULE = 'PAYROLL_SCHEDULE',
}

export enum EvidenceDeletedByType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  ADMIN = 'ADMIN',
}

export enum EvidenceDeletionReason {
  EXCLUDED_SENDER = 'EXCLUDED_SENDER',
  LOW_CONFIDENCE_SCORE = 'LOW_CONFIDENCE_SCORE',
  CLEARED_EMAIL_SCAN_EVIDENCE = 'CLEARED_EMAIL_SCAN_EVIDENCE',
  MARKED_AS_NOT_INVOICE = 'MARKED_AS_NOT_INVOICE',
  DUPLICATE = 'DUPLICATE',
}

/**
 * Enum of supported TPP adapters
 */
export enum TPPAdapters {
  TELL_MONEY_EU = 'TELL_MONEY_EU',
  TELL_MONEY_UK = 'TELL_MONEY_UK',
}

/**
 * A list of types is useful for filtering and searching audit logs
 * Feel free to add more as needed
 */
export enum AuditLogEvent {
  ACCOUNT_STATE_CHANGE = 'ACCOUNT_STATE_CHANGE',
  ACCOUNT_AND_TRANSACTION_SYNC = 'ACCOUNT_AND_TRANSACTION_SYNC',
  ACCOUNT_BALANCE_CHANGE = 'ACCOUNT_BALANCE_CHANGE',
  ACCOUNT_MAPPINGS_UPDATED = 'ACCOUNT_MAPPINGS_UPDATED',
  AI_PIPELINE = 'AI_PIPELINE',
  PAYMENT_AUTHORISATION = 'PAYMENT_AUTHORISATION',
  PAYMENT_CALLBACK = 'PAYMENT_CALLBACK',
  PAYMENT_STATUS_CHANGE = 'PAYMENT_STATUS_CHANGE',
  PAYMENT_EXTERNAL_LINKED = 'PAYMENT_EXTERNAL_LINKED',
  // Invoice related events
  INVOICE_PAID_CHANGED = 'INVOICE_PAID_CHANGED',
  INVOICE_READER = 'INVOICE_READER',
  INVOICE_SYNCED_TO_ACCOUNTING = 'INVOICE_SYNCED_TO_ACCOUNTING',
  INVOICE_SYNC_STATE_CHANGE = 'INVOICE_SYNC_STATE_CHANGE',
  // OAuth related events
  OAUTH_AUTHORIZATION_FAILED = 'OAUTH_AUTHORIZATION_FAILED',
  OAUTH_AUTHORIZATION_SUCCEEDED = 'OAUTH_AUTHORIZATION_SUCCEEDED',
  OAUTH_CALLBACK_FAILED = 'OAUTH_CALLBACK_FAILED',
  OAUTH_CALLBACK_SUCCEEDED = 'OAUTH_CALLBACK_SUCCEEDED',
  OAUTH_CONNECTION_FAILED = 'OAUTH_CONNECTION_FAILED',
  OAUTH_CONNECTION_SUCCEEDED = 'OAUTH_CONNECTION_SUCCEEDED',
  OAUTH_CONNECTION_REVOCATION_FAILED = 'OAUTH_CONNECTION_REVOCATION_FAILED',
  OAUTH_CONNECTION_REVOCATION_SUCCEEDED = 'OAUTH_CONNECTION_REVOCATION_SUCCEEDED',
  OAUTH_CONNECTION_CREDENTIALS_MISSING = 'OAUTH_CONNECTION_CREDENTIALS_MISSING',
  // Email invoice extraction
  EMAIL_INVOICE_EXTRACTOR = 'EMAIL_INVOICE_EXTRACTOR',
  EMAIL_INVOICE_PROCESSOR = 'EMAIL_INVOICE_PROCESSOR',
  // Beneficiary related events
  BENEFICIARY_UPDATE = 'BENEFICIARY_UPDATE',
}

/**
 * Takes a string and an enum type and returns the enum value.
 */
/* eslint-disable 
  @typescript-eslint/no-explicit-any, 
  @typescript-eslint/no-unsafe-member-access, 
  @typescript-eslint/no-unsafe-assignment, 
  @typescript-eslint/no-unsafe-return 
*/
export const StringToEnum = (str: string, enumType: any) => {
  const enumValue = enumType[str as keyof typeof enumType];
  if (enumValue === undefined) {
    throw new Error(`Invalid enum value: ${str}`);
  }
  return enumValue;
  /* eslint-enable */
};

/**
 * This enum serves as the single source of truth for all supported providers
 * across different domains (email, banking, accounting).
 */
export enum OAuthProvider {
  GMAIL = 'gmail',
  DUMMY = 'dummy',
  XERO = 'xero',
}

export enum ConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
  PENDING = 'pending',
}

export enum FeatureStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  COMING_SOON = 'coming_soon',
}

export enum ConnectionFeatureType {
  GMAIL_SEARCH = 'gmail_search',
  GMAIL_USER_PROFILE = 'gmail_user_profile',
  XERO_SYNC_SEAPOINT_BANK_FEED = 'xero_sync_seapoint_bank_feed',
  XERO_SYNC_INVOICES = 'xero_sync_invoices',
  XERO_STREAMLINE_RECONCILIATION = 'xero_streamline_reconciliation',
}

export enum ConnectionShareScope {
  USER = 'user',
  ORGANIZATION = 'organization',
}
/**
 * Enum for employee status
 */
export enum EmployeeStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export enum EmployeeDeletedByType {
  USER = 'USER',
  ADMIN = 'ADMIN',
}

/**
 * Enum for payroll status
 */
export enum PayrollStatus {
  IN_DRAFT = 'IN_DRAFT',
  WAITING_FOR_APPROVAL = 'WAITING_FOR_APPROVAL',
  APPROVED = 'APPROVED',
  PARTIALLY_SUCCEEDED = 'PARTIALLY_SUCCEEDED',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export enum GmailExtractionTimeRange {
  ONE_WEEK = '1 Week',
  ONE_MONTH = '1 Month',
  THREE_MONTHS = '3 Months',
  SIX_MONTHS = '6 Months',
  TWELVE_MONTHS = '12 Months',
}

// VoP CoP
export enum PayeeCheckType {
  VERIFY_PAYEE = 'VOP',
  CONFIRM_PAYEE = 'COP',
}

// VoP check response code
export enum VerifyPayeeCheckResponseCode {
  /**
   * The details provided matched the payee bank details.
   * You can proceed with the payment.
   * Remember, you should still exercise caution when sending payments. The service is an account name checking service and does not protect against all types of fraud.
   */
  MATCHED = 'MATCHED',

  /**
   * The account details provided do not match the payee bank details.
   * The account details should be checked carefully, you are advised not to proceed with the payment.
   * Authorising the payment could result in transferring the funds to a payment account not held by your intended recipient.
   * You should edit the details if possible, and submit another account verification check request before proceeding.
   */
  NO_MATCH = 'NO_MATCH',

  /**
   * The name provided is similar to the payee account name. The actual name is provided in matchedName.
   * The account details should be checked carefully.
   * If you decide to continue with the payment you should update the details before proceeding.
   */
  CLOSE_MATCH = 'CLOSE_MATCH',

  /**
   * The verification check is not possible.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  CHECK_NOT_POSSIBLE = 'CHECK_NOT_POSSIBLE',

  /**
   * The request was malformed, missing or non-compliant JSON body or URL parameters.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  INVALID = 'INVALID',

  /**
   * There is a technical error when trying to process the request.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  GENERAL = 'GENERAL',
  /**
   * This code is generated internally by Seapoint when the payee check is not possible to be verified.
   */
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

// CoP check response code
export enum ConfirmationOfPayeeCheckResponseCode {
  /**
   * Account name and account type match the name and accountType provided.
   * You can proceed with the creation of a beneficiary or payment.
   * Note: Exercise caution when sending payments. The service is an account name checking service and does not protect against all types of fraud.
   */
  MATCHED = 'MATCHED',

  /**
   * Account name and account type do not match the name and accountType provided.
   * The account details should be checked carefully, and you are advised not to proceed with the creation of the beneficiary or payment.
   * Edit the details if possible, and submit another account name check request before proceeding.
   */
  NOT_MATCHED = 'NOT_MATCHED',

  /**
   * The name provided is similar to the account name. The actual account name is provided in result.name.
   * The account details should be checked carefully. If you decide to continue with the creation of the beneficiary/payment
   * you should edit the details if necessary before proceeding.
   */
  CLOSE_MATCH = 'CLOSE_MATCH',

  /**
   * The account name matches the name provided but the account is a business account, not a personal account.
   * The account details should be checked carefully before proceeding.
   */
  BUSINESS_ACCOUNT_NAME_MATCHED = 'BUSINESS_ACCOUNT_NAME_MATCHED',

  /**
   * The account name matches the name provided but the account is a personal account, not a business account.
   * The account details should be checked carefully before proceeding.
   */
  PERSONAL_ACCOUNT_NAME_MATCHED = 'PERSONAL_ACCOUNT_NAME_MATCHED',

  /**
   * The name provided is similar to the account name and the account is a business account, not a personal account.
   * The actual account name is provided in result.name.
   * The account details should be checked carefully. If you decide to continue with the creation of the beneficiary/payment
   * you should edit the details if necessary before proceeding.
   */
  BUSINESS_ACCOUNT_CLOSE_MATCH = 'BUSINESS_ACCOUNT_CLOSE_MATCH',

  /**
   * The name provided is similar to the account name and the account is a personal account, not a business account.
   * The actual account name is provided in result.name.
   * The account details should be checked carefully. If you decide to continue with the creation of the beneficiary/payment
   * you should edit the details if necessary before proceeding.
   */
  PERSONAL_ACCOUNT_CLOSE_MATCH = 'PERSONAL_ACCOUNT_CLOSE_MATCH',

  /**
   * The account does not exist.
   * You should not proceed. The account details should be checked carefully.
   * Edit the details if possible and submit another account name check request before creating a beneficiary or payment.
   */
  ACCOUNT_DOES_NOT_EXIST = 'ACCOUNT_DOES_NOT_EXIST',

  /**
   * The secondaryAccountId is not valid.
   * The account details should be checked carefully. The account could not be identified using the secondary account reference.
   * Edit the details if possible and submit another account name check request before proceeding.
   */
  SECONDARY_ACCOUNT_ID_NOT_FOUND = 'SECONDARY_ACCOUNT_ID_NOT_FOUND',

  /**
   * The account does not support account name check requests.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  ACCOUNT_NOT_SUPPORTED = 'ACCOUNT_NOT_SUPPORTED',

  /**
   * The account has been switched using the Current Account Switching Service.
   * You should not proceed with the creation of a beneficiary or payment using these account details.
   * Obtain the new account details if possible, edit the details and submit another account name check request before creating a beneficiary or payment.
   */
  ACCOUNT_SWITCHED = 'ACCOUNT_SWITCHED',

  /**
   * The account name check cannot be completed for the account number and sort code provided.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  WRONG_PARTICIPANT = 'WRONG_PARTICIPANT',

  /**
   * The bank did not respond to the account name check request.
   * It is recommended you try the check again after some time.
   * If you do decide to continue with this result you should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  NO_RESPONSE = 'NO_RESPONSE',

  /**
   * The account name check could not be completed as the bank does not accept account name check requests.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  NOT_ENROLLED = 'NOT_ENROLLED',

  OPTED_OUT = 'OPTED_OUT',

  /**
   * This code is generated internally by Seapoint when the payee check is not possible to be verified.
   */
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}
